import { removeSurroundingChars } from '@maximizer/shared/util';

interface JsonStringScanState {
  inString: boolean;
  escaped: boolean;
}

interface QuotedSegmentScanState {
  insideQuotes: boolean;
  currentQuoteChar: string | null;
  escapeNext: boolean;
}

function advanceInsideJsonString(char: string, state: JsonStringScanState): void {
  if (state.escaped) {
    state.escaped = false;
    return;
  }

  if (char === '\\') {
    state.escaped = true;
    return;
  }

  if (char === '"') {
    state.inString = false;
  }
}

function appendQuotedSegmentChar(char: string, state: QuotedSegmentScanState, currentSegment: string): string {
  if (state.escapeNext) {
    state.escapeNext = false;
    return currentSegment + char;
  }

  if (char === '\\') {
    state.escapeNext = true;
    return currentSegment + char;
  }

  // Only double quotes delimit values. Apostrophes in unquoted prose (children's)
  // must not swallow the rest of the property list.
  if (char === '"') {
    if (!state.insideQuotes) {
      state.insideQuotes = true;
      state.currentQuoteChar = char;
    } else if (state.currentQuoteChar === char) {
      state.insideQuotes = false;
      state.currentQuoteChar = null;
    }
    return currentSegment + char;
  }

  return currentSegment + char;
}

/** Extracts a balanced `{...}` object, ignoring braces inside quoted strings. */
export function extractBalancedJsonObject(value: string, startIndex: number): string | null {
  if (value[startIndex] !== '{') {
    return null;
  }

  let depth = 0;
  const stringState: JsonStringScanState = { inString: false, escaped: false };

  for (let i = startIndex; i < value.length; i++) {
    const char = value[i];

    if (stringState.inString) {
      advanceInsideJsonString(char, stringState);
      continue;
    }

    if (char === '"') {
      stringState.inString = true;
      continue;
    }

    if (char === '{') {
      depth++;
      continue;
    }

    if (char === '}') {
      depth--;
      if (depth === 0) {
        return value.slice(startIndex, i + 1);
      }
    }
  }

  return null;
}

/** Pushes openers; pops on a matching closer. Returns false for a mismatched closer to drop. */
function acceptBracket(char: string, openers: string[]): boolean {
  if (char === '{' || char === '[') {
    openers.push(char);
    return true;
  }

  const expected = char === '}' ? '{' : '[';
  if (openers.at(-1) !== expected) {
    return false;
  }

  openers.pop();
  return true;
}

/**
 * LLM layouts sometimes emit an extra `}` (e.g. `]}},{"title":…}` between sections).
 * Drops closers that do not match the current open bracket so JSON.parse can proceed.
 */
export function removeMismatchedClosingBrackets(raw: string): string {
  const openers: string[] = [];
  const stringState: JsonStringScanState = { inString: false, escaped: false };
  let result = '';

  for (const char of raw) {
    if (stringState.inString) {
      result += char;
      advanceInsideJsonString(char, stringState);
      continue;
    }

    if (char === '"') {
      stringState.inString = true;
      result += char;
      continue;
    }

    if (char === '{' || char === '[' || char === '}' || char === ']') {
      if (acceptBracket(char, openers)) {
        result += char;
      }
      continue;
    }

    result += char;
  }

  return result;
}

/** Splits a key:value list by commas, respecting quoted segments. */
export function splitCommaSeparatedProperties(input: string): string[] {
  const properties: string[] = [];
  let currentProperty = '';
  const quoteState: QuotedSegmentScanState = {
    insideQuotes: false,
    currentQuoteChar: null,
    escapeNext: false,
  };

  for (const char of input) {
    if (char === ',' && !quoteState.insideQuotes) {
      const trimmed = currentProperty.trim();
      // Leading/double commas (e.g. `{ , title: … }`) leave empty segments — skip them.
      if (trimmed) {
        properties.push(trimmed);
      }
      currentProperty = '';
      continue;
    }

    currentProperty = appendQuotedSegmentChar(char, quoteState, currentProperty);
  }

  if (currentProperty.trim()) {
    properties.push(currentProperty.trim());
  }

  return properties;
}

/** Parses `key: value` segments into a map, unquoting values. */
export function parseKeyValuePropertyList(properties: string[]): Record<string, string> {
  const result: Record<string, string> = {};

  for (const prop of properties) {
    const colonIndex = prop.indexOf(':');
    if (colonIndex === -1) {
      continue;
    }

    const key = prop.substring(0, colonIndex).trim();
    let value = prop.substring(colonIndex + 1).trim();

    value = value.replaceAll('\\"', '"').replaceAll("\\'", "'");
    value = removeSurroundingChars(value, '"', '"');
    value = removeSurroundingChars(value, "'", "'");

    result[key] = value;
  }

  return result;
}

/** Parses loose markup property blocks like `(CRM: { type: "Note", … })`. */
export function parseLooseMarkupProperties(input: string): Record<string, string> {
  let content = input.trim();
  content = removeSurroundingChars(content, '{', '}');
  content = removeSurroundingChars(content, '(', ')');

  return parseKeyValuePropertyList(splitCommaSeparatedProperties(content));
}
